import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, PhoneIcon, MapPinIcon, MailIcon, Loader2Icon } from 'lucide-react';
import { COMPANY } from '../../lib/site';
import { SectionHeading } from '../ui/SectionHeading';
import { LazyMap } from '../ui/LazyMap';
import { Reveal } from '../fx/Reveal';
type Status = 'idle' | 'loading' | 'done';
type FieldName = 'name' | 'email' | 'phone';
type ContactForm = {
  name: string;
  email: string;
  phone: string;
  message: string;
};
const fields: Array<{
  name: FieldName;
  label: string;
  type: string;
  placeholder: string;
}> = [{
  name: 'name',
  label: 'Full Name',
  type: 'text',
  placeholder: 'Jane Doe'
}, {
  name: 'email',
  label: 'Email',
  type: 'email',
  placeholder: 'jane@email.com'
}, {
  name: 'phone',
  label: 'Phone',
  type: 'tel',
  placeholder: '(645) 216-5704'
}];
const houstonEmbedUrl = 'https://www.openstreetmap.org/export/embed.html?bbox=-95.8%2C29.5%2C-95.0%2C30.1&layer=mapnik&marker=29.7604%2C-95.3698';
const houstonMapsUrl = 'https://www.google.com/maps/search/?api=1&query=Houston%2C%20Texas';
export function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (status !== 'idle') return;
    setStatus('loading');
    window.setTimeout(() => {
      setStatus('done');
      window.setTimeout(() => {
        setStatus('idle');
        setForm({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }, 2500);
    }, 1400);
  };
  return <section className="deferred-section relative mx-auto max-w-7xl px-6 py-24" data-id="element-177" data-mp="n2sgrwgkuedx">
      <SectionHeading eyebrow="Get In Touch" title="Let's Protect Your Home" subtitle="Schedule a free inspection or reach us any time for an emergency. We respond fast." data-id="element-178" data-mp="r3vvugqsw6e3" />

      <div className="mt-14 grid gap-8 lg:grid-cols-2" data-id="element-179" data-mp="2s527wlqziu1p">
        <Reveal data-id="element-180" data-mp="2o3gu8y6mmbqg">
          <div className="glass-strong flex h-full flex-col overflow-hidden rounded-[2.5rem] shadow-glass" data-id="element-181" data-mp="2o3yo5o5tgdv7">
            <LazyMap title="Dream Shield service area map" embedUrl={houstonEmbedUrl} mapsUrl={houstonMapsUrl} className="h-64 w-full lg:h-80" data-id="element-182" data-mp="31v3sh3k4pubv" />
            <div className="grid flex-1 gap-4 p-8" data-id="element-183" data-mp="3bqyjalyiavpq">
              <a href={COMPANY.phoneHref} className="flex items-center gap-4 rounded-2xl bg-white/60 p-4 transition-colors hover:bg-white" data-id="element-184" data-mp="36ty9q12g7atv">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-fire/10 text-fire" data-id="element-185" data-mp="3fucy59apl61l">
                  <PhoneIcon className="h-5 w-5" data-id="element-186" data-mp="1qenab2h6wzlb" />
                </span>
                <span data-id="element-187" data-mp="1lg3m0vf4jbps">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-188" data-mp="1lglq2e812u89">
                    24/7 Phone
                  </span>
                  <span className="font-semibold text-navy" data-id="element-189" data-mp="ykxobfejfdb">
                    {COMPANY.phoneDisplay}
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl bg-white/60 p-4" data-id="element-190" data-mp="3obx0llyxuryn">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-royal/10 text-royal" data-id="element-191" data-mp="3sc76yzqn7ke1">
                  <MapPinIcon className="h-5 w-5" data-id="element-192" data-mp="3ebwghxt8w7te" />
                </span>
                <span data-id="element-193" data-mp="3jbr9c6wkz7h3">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-194" data-mp="3jd2as02k0351">
                    Service Area
                  </span>
                  <span className="font-semibold text-navy" data-id="element-195" data-mp="35ecg0r37ifju">
                    {COMPANY.serviceArea}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-2xl bg-white/60 p-4" data-id="element-196" data-mp="13m7p9ixg1oap">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-success/10 text-success" data-id="element-197" data-mp="17lgi8lknti57">
                  <MailIcon className="h-5 w-5" data-id="element-198" data-mp="ivbwtuf827hl" />
                </span>
                <span data-id="element-199" data-mp="dwhgr08n2x16">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-200" data-mp="dxadr9hsq864">
                    Email
                  </span>
                  <span className="font-semibold text-navy" data-id="element-201" data-mp="8zh6ed4rb849">
                    hello@dreamshield.com
                  </span>
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-202" data-mp="3rbx6ldfoh4lr">
          <form onSubmit={submit} className="glass-strong rounded-[2.5rem] p-8 shadow-glass sm:p-10" data-id="element-203" data-mp="3ravleg4y71qb">
            <div className="grid gap-5" data-id="element-204" data-mp="3mag6hk50pm68">
              {fields.map(field => <div key={field.name} className="group" data-id="element-205" data-mp="3hfaqf1yar1q2">
                  <label htmlFor={field.name} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate" data-id="element-206" data-mp="3cemnpj0pgwg0">
                    {field.label}
                  </label>
                  <input id={field.name} type={field.type} required={field.name !== 'phone'} placeholder={field.placeholder} value={form[field.name]} onChange={event => setForm({
                ...form,
                [field.name]: event.target.value
              })} className="w-full rounded-2xl border border-royal/15 bg-white/70 px-4 py-3.5 text-sm text-navy outline-none transition-all placeholder:text-slate/60 focus:border-royal focus:shadow-[0_0_0_4px_rgba(0,87,255,0.12)]" data-id="element-207" data-mp="1nwtmd2erx9f6" />
                </div>)}
              <div data-id="element-208" data-mp="lh1qskfannd4">
                <label htmlFor="message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate" data-id="element-209" data-mp="lfgy70cjiehw">
                  How can we help?
                </label>
                <textarea id="message" required rows={4} placeholder="Tell us about your project or emergency..." value={form.message} onChange={event => setForm({
                ...form,
                message: event.target.value
              })} className="w-full resize-none rounded-2xl border border-royal/15 bg-white/70 px-4 py-3.5 text-sm text-navy outline-none transition-all placeholder:text-slate/60 focus:border-royal focus:shadow-[0_0_0_4px_rgba(0,87,255,0.12)]" data-id="element-210" data-mp="icau7w8hc9gl" />
              </div>
              <motion.button type="submit" data-cursor="button" disabled={status !== 'idle'} animate={{
              backgroundColor: status === 'done' ? '#19C37D' : '#0057FF',
              width: status === 'idle' ? '100%' : status === 'loading' ? '60%' : '64px'
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="mx-auto flex h-14 items-center justify-center gap-2 self-center overflow-hidden rounded-full font-semibold text-white shadow-glow" data-id="element-211" data-mp="4h8phcfdcq5d">
                {status === 'idle' && <span data-id="element-212" data-mp="3lj3601vyflmk">Schedule Free Inspection</span>}
                {status === 'loading' && <Loader2Icon className="h-6 w-6 animate-spin" data-id="element-213" data-mp="37o13zpu8agf1" />}
                {status === 'done' && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} data-id="element-214" data-mp="49euh7u7u03l">
                    <CheckIcon className="h-6 w-6" strokeWidth={3} data-id="element-215" data-mp="2541mhaffsey2" />
                  </motion.span>}
              </motion.button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>;
}